import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Package, Coin, TrendUp, Storefront, Tag, ShoppingCart, Receipt, UsersThree, WarningCircle, Clock } from "@phosphor-icons/react";
import { useAuth, hasRole } from "@/context/AuthContext";
import MyTasksWidget from "@/components/MyTasksWidget";
import InventoryTiersSection from "@/components/dashboard/InventoryTiersSection";

function KPI({ label, value, sub, icon: Icon, color = "#002FA7", testid }) {
  return (
    <div className="border border-zinc-200 p-5 bg-white hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-mono-data tracking-wider text-zinc-500 uppercase">{label}</div>
        <Icon size={20} weight="duotone" color={color} />
      </div>
      <div className="numeric text-3xl font-bold mt-3 text-zinc-900">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1 numeric">{sub}</div>}
    </div>
  );
}

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/reports/summary").then((r)=>setData(r.data)).catch(()=>{}); }, []);
  if (!data) return <div className="p-8 text-zinc-500">Cargando datos…</div>;

  // A user can have multiple roles. We render each role's section additively so
  // a sales+inventory user sees both KPI blocks.
  const isAdmin = hasRole(user, "super_admin");
  const showInventory = isAdmin || hasRole(user, "inventory");
  const showSales = isAdmin || hasRole(user, "sales");
  const showOnlyMarketing = !isAdmin && !showInventory && !showSales && hasRole(user, "marketing");

  return (
    <div className="p-6 sm:p-8 space-y-8">
      <header>
        <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">PANEL PRINCIPAL</div>
        <h1 className="font-heading text-4xl font-extrabold tracking-tight">Hola {user.name.split(" ")[0]}</h1>
        <p className="text-zinc-500 mt-1 text-sm">
          {isAdmin && <>Resumen general del inventario · TC: <span className="numeric">1 USD = {data.exchange_rate} MXN</span></>}
          {!isAdmin && showInventory && showSales && <>Inventario y actividad de ventas en un solo lugar</>}
          {!isAdmin && showInventory && !showSales && <>Estado del inventario · {data.total_products} productos · {data.total_items} unidades</>}
          {!isAdmin && !showInventory && showSales && <>Tu actividad de ventas y clientes</>}
          {showOnlyMarketing && <>Sube y administra fotos de productos</>}
        </p>
      </header>

      {!isAdmin && <MyTasksWidget />}

      {isAdmin && (
        <>
          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <KPI label="Productos" value={data.total_products} sub={`${data.total_items} unidades`} icon={Package} testid="kpi-products" />
            <KPI label="Valor a Costo" value={`$${fmt(data.total_cost_usd)}`} sub={`USD · MX$${fmt(data.total_cost_mxn)}`} icon={Coin} color="#52525B" testid="kpi-cost" />
            <KPI label="Utilidad potencial" value={`$${fmt(data.potential_profit_usd)}`} sub={`USD · MX$${fmt(data.potential_profit_usd * data.exchange_rate)}`} icon={TrendUp} color="#00873D" testid="kpi-profit" />
          </section>
          <section>
            <h2 className="font-heading text-xl font-bold mb-3">Valor de inventario por tier</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <KPI label="Precio Mínimo" value={`MX$${fmt(data.total_min_mxn)}`} sub={`USD $${fmt(data.total_min_usd)}`} icon={Tag} color="#E0000F" testid="kpi-min" />
              <KPI label="Reventa" value={`MX$${fmt(data.total_reseller_mxn)}`} sub={`USD $${fmt(data.total_reseller_usd)}`} icon={Tag} color="#7E57C2" testid="kpi-reseller" />
              <KPI label="Distribuidor" value={`MX$${fmt(data.total_marketplace_mxn)}`} sub={`USD $${fmt(data.total_marketplace_usd)}`} icon={ShoppingCart} color="#FFC000" testid="kpi-marketplace" />
              <KPI label="Tienda" value={`MX$${fmt(data.total_retail_mxn)}`} sub={`USD $${fmt(data.total_retail_usd)}`} icon={Storefront} color="#002FA7" testid="kpi-retail" />
            </div>
          </section>
          <InventoryTiersSection />
        </>
      )}

      {!isAdmin && showSales && (
        <section className="space-y-4">
          <div className="flex items-center gap-3">
            <h2 className="font-heading text-2xl font-bold">Ventas</h2>
            <div className="flex-1 h-px bg-zinc-200"/>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <KPI label="Notas creadas" value={data.sales_total_count || 0} icon={Receipt} testid="kpi-sales-count" />
            <KPI label="Ingresos generados" value={`MX$${fmt(data.sales_total_revenue_mxn)}`} sub={`USD $${fmt(data.sales_total_revenue_usd)}`} icon={Coin} color="#00873D" testid="kpi-sales-rev" />
            <KPI label="Clientes en CRM" value={data.clients_count || 0} icon={UsersThree} color="#FFC000" testid="kpi-clients" />
          </div>
        </section>
      )}

      {showSales && !isAdmin && data.receivables && (
        <section className="space-y-3">
          <div>
            <h3 className="font-heading text-lg font-semibold">Mis cuentas por cobrar</h3>
            <p className="text-[11px] text-zinc-500">Saldos pendientes de las notas que tú creaste.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="border border-zinc-200 p-4 bg-white">
              <div className="text-[10px] font-mono-data uppercase text-zinc-500">Por cobrar (vigente)</div>
              <div className="numeric text-xl font-bold mt-1.5">MX${fmt(data.receivables.current_mxn)}</div>
              <div className="text-[10px] text-zinc-400 numeric">USD ${fmt(data.receivables.current_usd)}</div>
              <div className="text-[10px] text-zinc-500 mt-1">{data.receivables.current_count} nota(s)</div>
            </div>
            <div className="border border-[#E0000F]/30 bg-[#E0000F]/5 p-4">
              <div className="text-[10px] font-mono-data uppercase text-[#E0000F]">Vencido</div>
              <div className="numeric text-xl font-bold mt-1.5 text-[#E0000F]">MX${fmt(data.receivables.overdue_mxn)}</div>
              <div className="text-[10px] text-[#E0000F]/80 numeric">USD ${fmt(data.receivables.overdue_usd)}</div>
              <div className="text-[10px] text-[#E0000F]/80 mt-1">{data.receivables.overdue_count} nota(s)</div>
            </div>
            <div className="border border-[#002FA7]/30 bg-[#002FA7]/5 p-4 lg:col-span-2">
              <div className="text-[10px] font-mono-data uppercase text-[#002FA7]">Total por cobrar</div>
              <div className="numeric text-2xl font-bold mt-1.5 text-[#002FA7]">MX${fmt(data.receivables.total_mxn)}</div>
              <div className="text-[10px] text-[#002FA7]/80 numeric">USD ${fmt(data.receivables.total_usd)}</div>
            </div>
          </div>

          <div className="border border-zinc-200 bg-white overflow-x-auto" data-testid="sales-receivables-table">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="text-left px-4 py-2.5">Folio</th>
                  <th className="text-left px-4 py-2.5">Cliente</th>
                  <th className="text-left px-4 py-2.5">Vencimiento</th>
                  <th className="text-right px-4 py-2.5">Pagado</th>
                  <th className="text-right px-4 py-2.5">Saldo</th>
                  <th className="text-left px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {data.receivables.items.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-zinc-400 dotted-bg text-xs">No tienes notas con saldo pendiente 🎉</td></tr>
                )}
                {data.receivables.items.map((r) => (
                  <tr key={r.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                    <td className="px-4 py-2.5 font-mono-data font-semibold">#{String(r.folio).padStart(6,"0")}</td>
                    <td className="px-4 py-2.5">{r.client_name}</td>
                    <td className="px-4 py-2.5 text-zinc-600">
                      {r.due_date}
                      {r.is_overdue && r.days_overdue > 0 && (
                        <span className="ml-2 text-[10px] text-[#E0000F] font-semibold inline-flex items-center gap-0.5">
                          <WarningCircle size={11}/> {r.days_overdue}d vencido
                        </span>
                      )}
                      {!r.is_overdue && (
                        <span className="ml-2 text-[10px] text-zinc-400 inline-flex items-center gap-0.5">
                          <Clock size={11}/> vigente
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right numeric text-zinc-500">{r.currency === "USD" ? "$" : "MX$"}{fmt(r.paid)}</td>
                    <td className={`px-4 py-2.5 text-right numeric font-bold ${r.is_overdue ? "text-[#E0000F]" : "text-zinc-900"}`}>{r.currency === "USD" ? "$" : "MX$"}{fmt(r.outstanding)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <a href={`/sales`} className="text-xs text-[#002FA7] hover:underline">Ver →</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {showSales && !isAdmin && (
        <section className="border border-zinc-200 p-6 bg-white">
          <h3 className="font-heading text-lg font-semibold mb-4">Mis ventas recientes</h3>
          <div className="space-y-2 text-sm">
            {(data.recent_sales || []).length === 0 && <div className="text-zinc-400 dotted-bg py-8 text-center text-xs">Sin ventas todavía</div>}
            {(data.recent_sales || []).map((s) => (
              <div key={s.id} className="flex justify-between items-center px-3 py-2 border border-zinc-100 rounded-sm hover:bg-zinc-50">
                <div>
                  <div className="font-mono-data text-xs text-zinc-500">#{String(s.folio).padStart(6,"0")}</div>
                  <div className="font-medium">{s.client_snapshot?.display_name || s.client_snapshot?.name}</div>
                </div>
                <div className="numeric font-semibold text-[#002FA7]">{s.currency === "USD" ? "$" : "MX$"}{fmt(s.total)}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {!isAdmin && showInventory && (
        <section className="space-y-4 pt-2">
          <div className="flex items-center gap-3">
            <h2 className="font-heading text-2xl font-bold">Inventario</h2>
            <div className="flex-1 h-px bg-zinc-200"/>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <KPI label="Productos" value={data.total_products} icon={Package} testid="kpi-products" />
            <KPI label="Unidades en stock" value={data.total_items} icon={Package} color="#00873D" testid="kpi-items" />
            <KPI label="Categorías activas" value={data.by_category.length} icon={Tag} color="#FFC000" testid="kpi-cats" />
          </div>
        </section>
      )}
    </div>
  );
}
